import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './ChatBot.css';

/**
 * Floating chat widget that sits bottom-right on every page after login.
 * Answers analytics questions via POST /api/chat.php
 *
 * Usage: drop <ChatBot /> inside your logged-in app shell (e.g. AppInner).
 */
export default function ChatBot() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [sending, setSending]   = useState(false);
  const bodyRef = useRef(null);

  // Position state (offset from bottom-right)
  const [pos, setPos] = useState({ x: 20, y: 20 });
  const isDragging = useRef(false);
  const dragStart  = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const moveCount  = useRef(0);

  // Greet on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      send(null, 'greet');
    }
    // eslint-disable-next-line
  }, [open]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Hide chatbot when not logged in
  if (!user) return null;

  const send = async (overrideMsg, autoKey) => {
    const msg = overrideMsg ?? input.trim();
    if (!msg && !autoKey) return;
    setInput('');

    // Skip echo for the auto-greet
    if (!autoKey) {
      setMessages(m => [...m, { role: 'user', text: msg, ts: Date.now() }]);
    }
    setSending(true);

    try {
      const payload = autoKey === 'greet' ? { message: 'hi' } : { message: msg };
      const res = await api.chat.ask(payload.message);
      setMessages(m => [...m, {
        role: 'bot',
        text: res.answer,
        chart: res.chart,
        followups: res.followups,
        intent: res.intent,
        ts: Date.now(),
      }]);
    } catch (e) {
      setMessages(m => [...m, {
        role: 'bot',
        text: t('chatBot.fetchError', { error: e.message }),
        ts: Date.now(),
      }]);
    }
    setSending(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onClickFollowup = (text) => send(text);

  const clearChat = () => {
    setMessages([]);
    // Re-greet
    setTimeout(() => send(null, 'greet'), 50);
  };

  const onMouseDown = (e) => {
    isDragging.current = true;
    moveCount.current = 0;
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: pos.x,
      posY: pos.y
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    moveCount.current++;
    const dx = dragStart.current.mouseX - e.clientX;
    const dy = dragStart.current.mouseY - e.clientY;
    
    // Boundary check (optional but good)
    const newX = Math.max(10, Math.min(window.innerWidth - 60, dragStart.current.posX + dx));
    const newY = Math.max(10, Math.min(window.innerHeight - 60, dragStart.current.posY + dy));
    
    setPos({ x: newX, y: newY });
  };

  const onMouseUp = () => {
    isDragging.current = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  const onTouchStart = (e) => {
    isDragging.current = true;
    moveCount.current = 0;
    const touch = e.touches[0];
    dragStart.current = {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      posX: pos.x,
      posY: pos.y
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  const onTouchMove = (e) => {
    if (!isDragging.current) return;
    moveCount.current++;
    const touch = e.touches[0];
    const dx = dragStart.current.mouseX - touch.clientX;
    const dy = dragStart.current.mouseY - touch.clientY;
    
    const newX = Math.max(10, Math.min(window.innerWidth - 60, dragStart.current.posX + dx));
    const newY = Math.max(10, Math.min(window.innerHeight - 60, dragStart.current.posY + dy));
    
    setPos({ x: newX, y: newY });
    // Prevent scrolling while dragging
    if (e.cancelable) e.preventDefault();
  };

  const onTouchEnd = () => {
    isDragging.current = false;
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  };

  const toggleOpen = () => {
    // Only open if we didn't drag it (less than 5 pixels of movement)
    if (moveCount.current < 5) {
      setOpen(true);
    }
  };

  return (
    <>
      {/* Floating launch button */}
      {!open && (
        <button
          className="chatbot-launcher"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onClick={toggleOpen}
          style={{ 
            bottom: pos.y, 
            right: pos.x, 
            position: 'fixed', 
            transition: isDragging.current ? 'none' : 'bottom 0.3s, right 0.3s',
            cursor: isDragging.current ? 'grabbing' : 'grab',
            touchAction: 'none'
          }}
          title={t('chatBot.dragToMove')}
        >
          <span style={{ fontSize: 22 }}>💬</span>
          <span className="chatbot-launcher-dot" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="chatbot-panel" role="dialog" aria-label={t('chatBot.assistant')}>
          <div className="chatbot-header">
            <div>
              <div className="chatbot-title">💡 {t('chatBot.title')}</div>
              <div className="chatbot-subtitle">{t('chatBot.subtitle')}</div>
            </div>
            <div className="chatbot-header-btns">
              <button className="chatbot-iconbtn" onClick={clearChat} title={t('chatBot.clearChat')}>↺</button>
              <button className="chatbot-iconbtn" onClick={() => setOpen(false)} title={t('common.close')}>✕</button>
            </div>
          </div>

          <div className="chatbot-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <Message key={m.ts + '-' + i} msg={m} onFollowup={onClickFollowup} />
            ))}
            {sending && (
              <div className="chatbot-msg chatbot-msg-bot">
                <div className="chatbot-bubble chatbot-bubble-bot chatbot-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
          </div>

          <div className="chatbot-input-row">
            <textarea
              className="chatbot-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('chatBot.inputPlaceholder')}
              rows={1}
              disabled={sending}
            />
            <button
              className="chatbot-send"
              onClick={() => send()}
              disabled={sending || !input.trim()}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Message bubble ───────────────────────────────────────── */
function Message({ msg, onFollowup }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`chatbot-msg ${isUser ? 'chatbot-msg-user' : 'chatbot-msg-bot'}`}>
      <div className={`chatbot-bubble ${isUser ? 'chatbot-bubble-user' : 'chatbot-bubble-bot'}`}>
        <div className="chatbot-text">{msg.text}</div>

        {msg.chart && <InlineChart chart={msg.chart} />}

        {!isUser && msg.followups && msg.followups.length > 0 && (
          <div className="chatbot-chips">
            {msg.followups.map((f, i) => (
              <button key={i} className="chatbot-chip" onClick={() => onFollowup(f)}>{f}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tiny bar chart (no external deps) ────────────────────── */
function InlineChart({ chart }) {
  if (!chart || !chart.data || chart.data.length === 0) return null;
  const data = chart.data;
  const max = chart.max || Math.max(...data.map(d => Math.abs(Number(d.value) || 0)));
  const unit = chart.unit || '';
  const fmt = (v) => {
    if (unit === '%') return v.toFixed(0) + '%';
    if (Math.abs(v) >= 1000) return '₹' + (v / 1000).toFixed(1) + 'k';
    return '₹' + v.toFixed(0);
  };

  return (
    <div className="chatbot-chart">
      {chart.title && <div className="chatbot-chart-title">{chart.title}</div>}
      {data.map((d, i) => {
        const v = Number(d.value) || 0;
        const pct = max > 0 ? (Math.abs(v) / max) * 100 : 0;
        return (
          <div key={i} className="chatbot-chart-row">
            <div className="chatbot-chart-label" title={d.label}>{d.label}</div>
            <div className="chatbot-chart-track">
              <div
                className={`chatbot-chart-bar ${v < 0 ? 'chatbot-chart-bar-neg' : ''}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="chatbot-chart-value">{fmt(v)}</div>
          </div>
        );
      })}
    </div>
  );
}
